import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Volume2, VolumeX, Home, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { VW, VH, PAL, logoImg, pixelText, fmtTime } from "./engine";
import { drawFighter } from "./sprites";
import { Level1 } from "./level1";
import { Level2 } from "./level2";
import { Level3 } from "./level3";
import { sfx, setSoundEnabled, playMusic, stopMusic, loadMusicSlots, setMasterVolume } from "./audio";
import * as api from "./api";

/* JOH World — retro mini-game. State machine: slechts één state actief. */
const S = {
  READY: "READY", GEAR: "GEAR_CHECK", MISSING: "MISSING_GEAR", TITLE: "TITLE",
  L1_INTRO: "LEVEL_1_INTRO", L1: "LEVEL_1_PLAYING", L1_DONE: "LEVEL_1_COMPLETE",
  L2_INTRO: "LEVEL_2_INTRO", L2: "LEVEL_2_PLAYING", L2_FAIL: "LEVEL_2_FAILED", L2_DONE: "LEVEL_2_COMPLETE",
  L3_INTRO: "LEVEL_3_INTRO", L3: "LEVEL_3_PLAYING", L3_DONE: "LEVEL_3_COMPLETE",
  COMPLETE: "GAME_COMPLETE", NAME: "NAME_ENTRY", BOARD: "LEADERBOARD", FINAL: "FINAL",
};

const BTN = "jw-btn px-6 py-4 bg-[#FFC61A] text-[#0A0A0A] font-pixel text-[10px] tracking-wide border-b-4 border-[#8C6A00] active:border-b-0 active:translate-y-1 transition-[border,transform] cursor-pointer";
const BTN2 = "jw-btn px-6 py-4 bg-transparent text-[#F5F3F0] font-pixel text-[10px] border-2 border-[#F5F3F0]/40 hover:border-[#FFC61A] hover:text-[#FFC61A] transition-colors cursor-pointer";

export default function JohWorld() {
  const [params] = useSearchParams();
  const autoplay = params.get("autoplay") === "1" || params.get("autoplay") === "2";
  const lazyL2 = params.get("autoplay") === "2"; // test-variant: faal L2 bewust 1× (TRY AGAIN-pad)
  const [state, setState] = useState(S.READY);
  const [sound, setSound] = useState(true);
  const [vol, setVol] = useState(0.8);
  const [score, setScore] = useState(0);
  const [hearts, setHearts] = useState(3);
  const [timeStr, setTimeStr] = useState("00:00.00");
  const [finalTime, setFinalTime] = useState(null);
  const [submitResult, setSubmitResult] = useState(null);
  const [board, setBoard] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const canvasRef = useRef(null);
  const levelRef = useRef(null);
  const inputRef = useRef({ left: false, right: false, down: false, jumpEdge: false, duckEdge: false, b1Edge: false, b2Edge: false });
  const runRef = useRef({ id: null, assisted: autoplay });
  const timerRef = useRef({ start: null, end: null });
  const scoreRef = useRef(0);
  const stateRef = useRef(state);
  const l1ScoreRef = useRef(0);
  const pushupsRef = useRef(0);
  const l3AttemptsRef = useRef(0);
  const lazyFailRef = useRef({ waiting: lazyL2 });
  const tickRef = useRef(0);
  stateRef.current = state;

  useEffect(() => {
    document.title = "JOH World | JOH Boxing";
    loadMusicSlots();
    return () => { document.title = "JOH Boxing"; stopMusic(); };
  }, []);

  useEffect(() => { setSoundEnabled(sound); }, [sound]);
  useEffect(() => { setMasterVolume(vol); }, [vol]);

  const addScore = useCallback((n) => {
    scoreRef.current += n;
    setScore(scoreRef.current);
  }, []);

  /* ---------- master timer ---------- */
  const timerStart = () => { timerRef.current = { start: performance.now(), end: null }; };
  const timerFinish = () => { if (timerRef.current.start && !timerRef.current.end) timerRef.current.end = performance.now(); };
  const elapsed = () => {
    const t = timerRef.current;
    if (!t.start) return 0;
    return (t.end || performance.now()) - t.start;
  };

  /* ---------- run API (best-effort; game blijft spelen bij netwerkproblemen) ---------- */
  const progress = async (event, data) => {
    if (!runRef.current.id) return;
    try { await api.sendProgress(runRef.current.id, event, data); } catch { /* offline tolerant */ }
  };

  /* ---------- state helpers ---------- */
  const go = (s) => { setState(s); stateRef.current = s; };

  const startLevel1 = async () => {
    sfx.levelStart();
    playMusic("level1");
    levelRef.current = new Level1({
      onScore: addScore,
      onHearts: setHearts,
      onClear: (l1score) => {
        l1ScoreRef.current = l1score;
        progress("l1_complete", { score: l1score });
        go(S.L1_DONE);
        setTimeout(() => startLevel2(), 1400);
      },
    });
    await progress("l1_start");
    timerStart(); // master timer begint pas bij Level 1
    go(S.L1);
  };

  const pressStart = async () => {
    sfx.start();
    try {
      const r = await api.startRun(runRef.current.assisted);
      runRef.current.id = r.run_id;
    } catch { runRef.current.id = null; }
    go(S.L1_INTRO);
  };

  const startLevel2 = () => {
    sfx.levelStart();
    playMusic("level2");
    levelRef.current = new Level2({
      onScore: addScore,
      onPass: (pushups) => {
        pushupsRef.current = pushups;
        progress("l2_pass", { pushups });
        go(S.L2_DONE);
        setTimeout(() => startLevel3(), 1600);
      },
    });
    progress("l2_enter");
    go(S.L2);
  };

  const startLevel3 = () => {
    sfx.levelStart();
    playMusic("level3");
    levelRef.current = new Level3({
      onScore: addScore,
      onRound: (round) => progress("l3_round", { round }),
      onComplete: (attempts) => {
        l3AttemptsRef.current = attempts;
        timerFinish(); // master timer stopt direct na laatste correcte punch
        progress("l3_complete");
        setFinalTime(elapsed());
        addScore(100);
        sfx.gameComplete();
        playMusic("final");
        go(S.COMPLETE);
      },
    });
    progress("l3_enter");
    go(S.L3);
  };

  /* ---------- inputs ---------- */
  const pressPush = useCallback(() => {
    const lv = levelRef.current;
    if (stateRef.current === S.L2 && lv?.press) lv.press();
  }, []);

  const pressNum = useCallback((n) => {
    const lv = levelRef.current;
    if (stateRef.current === S.L3 && lv?.press) { lv.press(n); return; }
    if (stateRef.current === S.L2) { pressPush(); return; }
    const inp = inputRef.current;
    if (n === 1) inp.b1Edge = true; else inp.b2Edge = true;
  }, [pressPush]);

  // keyboard
  useEffect(() => {
    const down = (e) => {
      const k = e.key;
      if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", " "].includes(k)) e.preventDefault();
      const inp = inputRef.current;
      if (k === "ArrowLeft") inp.left = true;
      else if (k === "ArrowRight") inp.right = true;
      else if (k === "ArrowDown") { if (!inp.down) inp.duckEdge = true; inp.down = true; }
      else if (k === "ArrowUp") inp.jumpEdge = true;
      else if (k === "1") pressNum(1);
      else if (k === "2") pressNum(2);
      else if (k === "Enter") {
        if (stateRef.current === S.READY) document.querySelector('[data-testid="jw-ready-yes"]')?.click();
        else if (stateRef.current === S.TITLE) document.querySelector('[data-testid="jw-press-start"]')?.click();
        else if (stateRef.current === S.L1_INTRO) document.querySelector('[data-testid="jw-start-level1"]')?.click();
      }
    };
    const up = (e) => {
      const inp = inputRef.current;
      if (e.key === "ArrowLeft") inp.left = false;
      else if (e.key === "ArrowRight") inp.right = false;
      else if (e.key === "ArrowDown") inp.down = false;
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, [pressNum]);

  /* ---------- game loop ---------- */
  useEffect(() => {
    let raf;
    let last = performance.now();
    const loop = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const ctx = canvasRef.current?.getContext("2d");
      if (ctx) {
        ctx.imageSmoothingEnabled = false;
        const lv = levelRef.current;
        const st = stateRef.current;
        if (st === S.L1 && lv) {
          lv.update(dt, inputRef.current);
          lv.draw(ctx);
          drawHud(ctx);
        } else if ((st === S.L2 || st === S.L2_FAIL) && lv) {
          lv.update(dt);
          lv.draw(ctx);
          // pass → auto verder; fail → TRY AGAIN-overlay
          if (lv.result === "pass" && lv.phaseT > 3.2 && st !== S.L2_DONE) lv.finishPass();
          if (lv.result === "fail" && st !== S.L2_FAIL) go(S.L2_FAIL);
          drawHud(ctx, true);
        } else if (st === S.L3 && lv) {
          lv.update(dt);
          lv.draw(ctx);
          drawHud(ctx, true);
        } else {
          drawAttract(ctx, now / 1000);
        }
        // edges consumeren
        const inp = inputRef.current;
        inp.jumpEdge = inp.duckEdge = inp.b1Edge = inp.b2Edge = false;
      }
      if (timerRef.current.start && !timerRef.current.end) setTimeStr(fmtTime(elapsed()));
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  function drawHud(ctx, noHearts = false) {
    if (!noHearts) {
      for (let i = 0; i < 3; i++) {
        const x = 8 + i * 14;
        ctx.fillStyle = i < hearts ? PAL.red : "#3A3A41";
        ctx.fillRect(x + 1, 8, 4, 4); ctx.fillRect(x + 7, 8, 4, 4);
        ctx.fillRect(x, 10, 12, 4);
        ctx.fillRect(x + 2, 14, 8, 3);
        ctx.fillRect(x + 4, 17, 4, 2);
      }
    }
    pixelText(ctx, `SCORE ${scoreRef.current}`, VW - 8, 8, 7, PAL.yellow, "right");
    pixelText(ctx, `TIME ${fmtTime(elapsed())}`, VW / 2, 8, 7, PAL.cream, "center");
  }

  function drawAttract(ctx, t) {
    ctx.fillStyle = "#0A0A0A";
    ctx.fillRect(0, 0, VW, VH);
    // drijvende pixels / sterren
    for (let i = 0; i < 40; i++) {
      const x = (i * 97 + t * 12 * (1 + (i % 3))) % VW;
      const y = (i * 53) % VH;
      ctx.fillStyle = i % 4 === 0 ? PAL.yellow : "#2A2A2E";
      ctx.fillRect(Math.round(x), y, 2, 2);
    }
    // grondstrook
    ctx.fillStyle = "#17171B";
    ctx.fillRect(0, VH - 26, VW, 26);
    ctx.fillStyle = PAL.yellow;
    for (let i = 0; i < 12; i++) ctx.fillRect(((i * 40 - t * 60) % (VW + 40)) - 20, VH - 14, 18, 2);
    // mini fighter loopt mee
    drawFighter(ctx, 60 + Math.sin(t * 0.8) * 14, VH - 26, 26, "walk", t);
  }

  /* ---------- leaderboard ---------- */
  const loadBoard = async () => {
    try { const r = await api.fetchLeaderboard(10); setBoard(r.items); } catch { setBoard([]); }
  };

  // live leaderboard: ververs elke 5s zolang het eindscherm actief is
  useEffect(() => {
    if (state !== S.FINAL) return;
    loadBoard();
    const iv = setInterval(loadBoard, 5000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const submitScore = async () => {
    const clean = name.trim().slice(0, 16);
    if (!clean) { toast.error("Vul je fighter name in"); return; }
    sfx.menuCoin();
    setBusy(true);
    try {
      if (!runRef.current.id) throw new Error("Geen run-sessie");
      const r = await api.completeRun(runRef.current.id, {
        player_name: clean,
        score: scoreRef.current,
        level_1_score: l1ScoreRef.current,
        pushup_count: pushupsRef.current,
        level_3_attempts: l3AttemptsRef.current,
      });
      setSubmitResult(r);
      if (r.assisted) toast.info("Demo-run: niet op de leaderboard (autoplay)");
      if (r.accepted && r.rank === 1) sfx.newRecord();
      setBoard(r.top || []);
      go(S.FINAL);
    } catch (err) {
      toast.error(err.message || "Score opslaan mislukt");
    }
    setBusy(false);
  };

  /* ---------- autoplay-bot (speelt legitiem, echte tijd; run wordt als assisted gemarkeerd) ---------- */
  useEffect(() => {
    if (!autoplay) return;
    const iv = setInterval(() => {
      const st = stateRef.current;
      const lv = levelRef.current;
      const inp = inputRef.current;
      const click = (tid) => document.querySelector(`[data-testid="${tid}"]`)?.click();
      if (st === S.READY) click("jw-ready-yes");
      else if (st === S.GEAR) click("jw-gear-yes");
      else if (st === S.TITLE) click("jw-press-start");
      else if (st === S.L1_INTRO) click("jw-start-level1");
      else if (st === S.L1 && lv) {
        inp.right = true;
        const p = lv.player;
        // flyers: duck als er een nadert
        const flyer = lv.flyers.find((f) => f.x > p.x && f.x - p.x < 90);
        inp.down = !!flyer;
        // rocks: elke stoot/aanval telt (klein = 1, groot = 3 levens)
        const rock = lv.rocks.find((r) => !r.gone && r.x > p.x - 10 && r.x - p.x < 46);
        if (rock) { inp.b1Edge = true; inp.b2Edge = true; }
        const enemy = lv.enemies.find((e) => !e.dead && e.x > p.x - 10 && Math.abs(e.x - p.x) < 40);
        if (enemy && !rock) inp.b2Edge = true;
      } else if (st === S.L2_FAIL) {
        lazyFailRef.current.waiting = false; // na 1× falen gewoon slagen
        click("jw-l2-retry");
      } else if (st === S.L2 && lv) {
        tickRef.current += 1;
        if (lv.phase === "play" && !lazyFailRef.current.waiting && lv.pushups < 52) { lv.press(); lv.press(); } // stop net boven de 50-eis (servercap: 90)
      } else if (st === S.L3 && lv) {
        if (lv.phase === "input" && !lv.playerAttack) {
          const next = lv.combo[lv.inputIdx];
          if (next) lv.press(next);
        }
      } else if (st === S.COMPLETE) {
        click("jw-to-name");
      } else if (st === S.NAME) {
        setName((n) => n || `BOT${Math.floor(Math.random() * 90 + 10)}`);
        setTimeout(() => click("jw-submit-score"), 400);
      }
    }, 300);
    return () => clearInterval(iv);
  }, [autoplay]);

  /* ---------- controles (touch) ---------- */
  const hold = (key, v) => (e) => {
    e.preventDefault();
    inputRef.current[key] = v;
    if (v && key === "down") inputRef.current.duckEdge = true;
  };
  const tap = (fn) => (e) => { e.preventDefault(); fn(); };

  const inGame = [S.L1, S.L2, S.L2_FAIL, S.L3].includes(state);
  const dpadActive = state === S.L1;
  const punchActive = state === S.L1 || state === S.L3;
  const mobile = useIsMobile();

  return (
    <main className={mobile ? "fixed inset-0 bg-black text-[#F5F3F0] overflow-hidden select-none" : "min-h-screen bg-[#0A0A0A] text-[#F5F3F0] flex items-center justify-center py-4 px-2 sm:px-4 select-none"} style={mobile ? { touchAction: "none" } : undefined} data-testid="johworld-page">
      {/* Handheld-inspired frame (origineel ontwerp) — op mobiel: fullscreen, portret-geoptimaliseerd */}
      <div className={mobile ? "absolute inset-0" : "relative w-full max-w-[560px] rounded-[28px] border-2 border-[#2A2A2E] bg-gradient-to-b from-[#1C1C20] to-[#121214] p-3 sm:p-5 shadow-[0_20px_60px_rgba(0,0,0,0.8)]"} data-testid="jw-device">
        {/* bovenrand: brand + sound */}
        {!mobile && (
        <div className="flex items-center justify-between px-2 pb-2">
          <span className="font-pixel text-[8px] text-[#FFC61A]">JOH WORLD</span>
          <div className="flex items-center gap-2">
            <span className="font-pixel text-[6px] text-[#4A4A50]">v1.0</span>
            <input
              type="range" min="0" max="100" value={Math.round(vol * 100)}
              onChange={(e) => setVol(Number(e.target.value) / 100)}
              className="w-16 h-1 accent-[#FFC61A] cursor-pointer"
              title="Volume"
              data-testid="jw-volume-slider"
            />
            <button onClick={() => { const on = !sound; setSound(on); if (on) setTimeout(() => sfx.menuBlip(), 30); }} className="text-[#A3A3A3] hover:text-[#FFC61A] transition-colors p-1" data-testid="jw-sound-toggle" title={sound ? "SOUND OFF" : "SOUND ON"}>
              {sound ? <Volume2 size={15} /> : <VolumeX size={15} />}
            </button>
          </div>
        </div>
        )}

        {/* scherm met bezel + scanlines */}
        <div className={mobile ? "absolute inset-0 flex items-center justify-center bg-black overflow-hidden" : "relative rounded-xl border-4 border-[#0A0A0A] bg-black overflow-hidden"} data-testid="jw-screen">
          <canvas ref={canvasRef} width={VW} height={VH} className={mobile ? "block" : "w-full block"} style={mobile ? { imageRendering: "pixelated", width: "100vw", height: "56.25vw", maxWidth: "177.78dvh", maxHeight: "100dvh" } : { imageRendering: "pixelated", aspectRatio: "16/9" }} data-testid="jw-canvas" />
          <div className="pointer-events-none absolute inset-0" style={{ background: "repeating-linear-gradient(0deg, rgba(0,0,0,0.18) 0 1px, transparent 1px 3px)" }} />
          <div className="pointer-events-none absolute inset-0" style={{ boxShadow: "inset 0 0 40px rgba(0,0,0,0.55)" }} />
        </div>

        {/* ------- overlays per state (op device-niveau: volledige ruimte, ook onder het canvas) ------- */}
          {state === S.READY && (
            <Overlay testid="jw-ready">
              <Tiny>WELKOM</Tiny>
              <Big>ARE YOU READY TO START YOUR JOURNEY AT YOUR NEW GYM?</Big>
              <button className={BTN} onClick={() => { sfx.menuGo(); playMusic("intro"); go(S.GEAR); }} data-testid="jw-ready-yes">YES!</button>
            </Overlay>
          )}

          {state === S.GEAR && (
            <Overlay testid="jw-gear">
              <Tiny>GEAR CHECK</Tiny>
              <Big>WELL, WE'RE HERE TO HELP YOU PREPARE.</Big>
              <Mid>DO YOU HAVE EVERYTHING YOU NEED?</Mid>
              <div className="grid grid-cols-4 gap-2 my-3" data-testid="jw-gear-items">
                <GearIcon label="GLOVES"><GearGloves /></GearIcon>
                <GearIcon label="WRAPS"><GearWraps /></GearIcon>
                <GearIcon label="GUARD"><GearGuard /></GearIcon>
                <GearIcon label="WATER"><GearWater /></GearIcon>
              </div>
              <div className="flex gap-3 flex-wrap justify-center">
                <button className={BTN} onClick={() => { sfx.menuCoin(); go(S.TITLE); }} data-testid="jw-gear-yes">YES, OF COURSE!</button>
                <button className={BTN2} onClick={() => { sfx.menuBack(); go(S.MISSING); }} data-testid="jw-gear-no">NO, SORRY</button>
              </div>
            </Overlay>
          )}

          {state === S.MISSING && (
            <Overlay testid="jw-missing">
              <Big>NO WORRIES!</Big>
              <Mid>Just reach out to the coach and he'll take it from there.</Mid>
              <Tiny>Once you're sorted, your JOH journey can begin.</Tiny>
              <button className={BTN} onClick={() => { sfx.menuBack(); go(S.GEAR); }} data-testid="jw-gear-back">BACK TO GEAR CHECK</button>
            </Overlay>
          )}

          {state === S.TITLE && (
            <Overlay testid="jw-title">
              <Tiny>PERFECT.</Tiny>
              <Mid>WELL, NOW LET'S GET YOU READY.</Mid>
              <Logo />
              <Tiny>PLAYER 1 READY</Tiny>
              <button className={`${BTN} animate-pulse`} onClick={pressStart} data-testid="jw-press-start">PRESS START</button>
            </Overlay>
          )}

          {state === S.L1_INTRO && (
            <Overlay testid="jw-l1-intro">
              <Tiny>LEVEL 1</Tiny>
              <Big>ROAD TO JOH GYM</Big>
              <Mid>Make your way to the gym. Duck the flying punches, clear anything in your way and reach the JOH flag.</Mid>
              <Tiny>← → MOVE · ↑ JUMP · ↓ DUCK · 1 JAB · 2 CROSS</Tiny>
              <button className={BTN} onClick={startLevel1} data-testid="jw-start-level1">START LEVEL 1</button>
            </Overlay>
          )}

          {state === S.L1_DONE && (
            <Overlay testid="jw-l1-done" dim>
              <Big>YOU MADE IT TO JOH GYM.</Big>
              <Tiny>WELCOME INSIDE...</Tiny>
            </Overlay>
          )}

          {state === S.L2_FAIL && (
            <Overlay testid="jw-l2-fail" dim>
              <button className={BTN} onClick={() => { sfx.menuGo(); levelRef.current?.retry(); go(S.L2); }} data-testid="jw-l2-retry">TRY AGAIN</button>
            </Overlay>
          )}

          {state === S.L2_DONE && (
            <Overlay testid="jw-l2-done" dim>
              <Big>PASSED!</Big>
              <Tiny>NAAR DE HEAVY BAG...</Tiny>
            </Overlay>
          )}

          {state === S.COMPLETE && (
            <Overlay testid="jw-complete">
              <Logo />
              <Big>JOH WORLD COMPLETE</Big>
              <Tiny>YOU DID IT!</Tiny>
              <Mid>YOU'RE READY FOR YOUR NEW GYM.</Mid>
              <div className="text-center my-2">
                <Tiny>YOUR TIME</Tiny>
                <div className="font-pixel text-[#FFC61A] text-lg" data-testid="jw-final-time">{fmtTime(finalTime || 0)}</div>
                <Tiny>YOUR SCORE</Tiny>
                <div className="font-pixel text-[#F5F3F0] text-sm" data-testid="jw-final-score">{score.toLocaleString("nl-NL")} PTS</div>
              </div>
              <button className={BTN} onClick={() => { sfx.menuGo(); go(S.NAME); }} data-testid="jw-to-name">ENTER YOUR FIGHTER NAME</button>
            </Overlay>
          )}

          {state === S.NAME && (
            <Overlay testid="jw-name">
              <Big>ENTER YOUR FIGHTER NAME</Big>
              <input
                value={name}
                onChange={(e) => setName(e.target.value.replace(/[^A-Za-zÀ-ÿ0-9 ._'!\-]/g, "").slice(0, 16))}
                maxLength={16}
                placeholder="FIGHTER NAME"
                className="w-full max-w-[260px] bg-[#0A0A0A] border-2 border-[#FFC61A] text-[#F5F3F0] font-pixel text-[11px] px-3 py-3 text-center outline-none placeholder:text-[#4A4A50]"
                data-testid="jw-name-input"
                autoFocus
              />
              <Tiny>{16 - name.length} TEKENS OVER</Tiny>
              <button className={BTN} disabled={busy} onClick={submitScore} data-testid="jw-submit-score">{busy ? "..." : "SUBMIT SCORE"}</button>
            </Overlay>
          )}

          {state === S.FINAL && (
            <Overlay testid="jw-final">
              <div className="overflow-y-auto max-h-full w-full flex flex-col items-center gap-1.5 py-2">
                <img src="/johworld/logo.png" alt="JOH World" className="w-32 sm:w-36" style={{ imageRendering: "pixelated" }} data-testid="jw-logo" />
                {submitResult && (
                  <div className="text-center" data-testid="jw-rank-msg">
                    {submitResult.assisted ? (
                      <Mid>DEMO-RUN (AUTOPLAY) — NIET GERANKT</Mid>
                    ) : submitResult.rank === 1 ? (
                      <Mid accent>NEW JOH WORLD RECORD!</Mid>
                    ) : submitResult.rank <= 10 ? (
                      <>
                        <Mid accent>YOU MADE THE TOP 10!</Mid>
                        <Mid>YOU'RE #{submitResult.rank}</Mid>
                      </>
                    ) : (
                      <>
                        <Mid>GOOD RUN!</Mid>
                        <Tiny>YOUR TIME: {fmtTime(submitResult.time_ms)}</Tiny>
                      </>
                    )}
                  </div>
                )}
                <div className="w-full max-w-[300px]" data-testid="jw-leaderboard">
                  <div className="flex items-center justify-center gap-2 mb-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E8362A] animate-pulse inline-block" data-testid="jw-live-dot" />
                    <Tiny>FASTEST FIGHTERS · LIVE TOP 10</Tiny>
                  </div>
                  {(board || []).length === 0 && <Tiny>NOG GEEN FIGHTERS — WEES DE EERSTE!</Tiny>}
                  <div className="max-h-[92px] overflow-y-auto" data-testid="jw-leaderboard-rows">
                  {(board || []).map((r) => {
                    const own = submitResult && !submitResult.assisted && r.rank === submitResult.rank;
                    return (
                      <div key={r.rank} className={`flex justify-between items-center py-0.5 border-b border-[#F5F3F0]/10 font-pixel text-[8px] ${own ? "text-[#FFC61A] bg-[#FFC61A]/10 px-1" : ""}`} data-testid={`jw-board-row-${r.rank}`}>
                        <span>{["🥇", "🥈", "🥉"][r.rank - 1] || `#${r.rank}`} {r.name}</span>
                        <span className="text-[#FFC61A]">{fmtTime(r.time_ms)}</span>
                      </div>
                    );
                  })}
                  </div>
                </div>
                <Mid>YOUR DIGITAL TRAINING IS COMPLETE. THAT MEANS YOU ARE READY.</Mid>
                <Big>THANK YOU FOR PLAYING, SEE YOU IN THE GYM.</Big>
                <div className="flex gap-2 flex-wrap justify-center">
                  <button className="jw-btn px-4 py-2.5 bg-[#FFC61A] text-[#0A0A0A] font-pixel text-[9px] tracking-wide border-b-4 border-[#8C6A00] active:border-b-0 active:translate-y-1 transition-[border,transform] cursor-pointer" onClick={() => { sfx.menuGo(); window.location.href = "/johworld"; }} data-testid="jw-play-again"><RotateCcw size={12} className="inline mr-1" />PLAY AGAIN</button>
                  <button className="jw-btn px-4 py-2.5 bg-transparent text-[#F5F3F0] font-pixel text-[9px] border-2 border-[#F5F3F0]/40 hover:border-[#FFC61A] hover:text-[#FFC61A] transition-colors cursor-pointer" onClick={() => { sfx.menuBack(); window.location.href = "/"; }} data-testid="jw-back-home"><Home size={12} className="inline mr-1" />BACK TO JOH BOXING</button>
                </div>
                <div className="font-pixel text-[10px] text-[#FFC61A] mt-1" data-testid="jw-brand-line">WITH PURPOSE, BY HEART.</div>
              </div>
            </Overlay>
          )}

        {/* ------- controls (desktop/handheld) ------- */}
        {!mobile && (
        <div className="mt-3 sm:mt-4" data-testid="jw-controls">
          {state === S.L2 || state === S.L2_FAIL ? (
            <button
              className="w-full py-8 rounded-2xl bg-[#E8362A] border-b-8 border-[#8C2318] font-pixel text-[#F5F3F0] text-xl active:border-b-0 active:translate-y-2 transition-[border,transform] cursor-pointer touch-none"
              onPointerDown={tap(pressPush)}
              data-testid="jw-push-btn"
            >PUSH!</button>
          ) : (
            <div className="flex items-center justify-between gap-4">
              {/* D-pad */}
              <div className={`grid grid-cols-3 gap-1 transition-opacity ${dpadActive ? "" : "opacity-30 pointer-events-none"}`} data-testid="jw-dpad">
                <span />
                <PadBtn label="↑" testid="jw-dpad-up" onDown={() => { inputRef.current.jumpEdge = true; }} onUp={() => {}} />
                <span />
                <PadBtn label="←" testid="jw-dpad-left" onDown={() => hold("left", true)()} onUp={() => hold("left", false)()} holdKey="left" />
                <PadBtn label="↓" testid="jw-dpad-down" onDown={() => hold("down", true)()} onUp={() => hold("down", false)()} holdKey="down" />
                <PadBtn label="→" testid="jw-dpad-right" onDown={() => hold("right", true)()} onUp={() => hold("right", false)()} holdKey="right" />
              </div>
              {/* actieknoppen */}
              <div className={`flex items-center gap-3 transition-opacity ${punchActive ? "" : "opacity-30 pointer-events-none"}`}>
                <div className="text-center">
                  <button
                    className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full bg-[#F5F3F0] text-[#0A0A0A] font-pixel text-lg border-b-8 border-[#9A968E] active:border-b-0 active:translate-y-1.5 transition-[border,transform] cursor-pointer touch-none"
                    onPointerDown={tap(() => pressNum(1))}
                    data-testid="jw-btn-1"
                  >1</button>
                  <div className="font-pixel text-[7px] text-[#A3A3A3] mt-1.5">JAB</div>
                </div>
                <div className="text-center">
                  <button
                    className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full bg-[#E8362A] text-[#F5F3F0] font-pixel text-lg border-b-8 border-[#8C2318] active:border-b-0 active:translate-y-1.5 transition-[border,transform] cursor-pointer touch-none"
                    onPointerDown={tap(() => pressNum(2))}
                    data-testid="jw-btn-2"
                  >2</button>
                  <div className="font-pixel text-[7px] text-[#A3A3A3] mt-1.5">CROSS</div>
                </div>
              </div>
            </div>
          )}
          <div className="hidden sm:block text-center font-pixel text-[6px] text-[#4A4A50] mt-3">
            KEYBOARD: ← → BEWEEG · ↑ SPRING · ↓ DUK · 1 JAB · 2 CROSS
          </div>
        </div>
        )}
      </div>

      {/* mobiel: zwevende sound-toggle + transparante touch-controls over het spelbeeld */}
      {mobile && (
        <>
          <div className="absolute top-2 right-2 z-30 flex flex-col items-end gap-1.5">
            <button onClick={() => { const on = !sound; setSound(on); if (on) setTimeout(() => sfx.menuBlip(), 30); }} className="text-white/60 hover:text-[#FFC61A] transition-colors p-2" data-testid="jw-sound-toggle" title={sound ? "SOUND OFF" : "SOUND ON"}>
              {sound ? <Volume2 size={18} /> : <VolumeX size={18} />}
            </button>
            <input
              type="range" min="0" max="100" value={Math.round(vol * 100)}
              onChange={(e) => setVol(Number(e.target.value) / 100)}
              className="w-20 h-1 accent-[#FFC61A] cursor-pointer"
              title="Volume"
              data-testid="jw-volume-slider"
            />
          </div>
          {inGame && <MobileControls state={state} inputRef={inputRef} pressNum={pressNum} pressPush={pressPush} />}
        </>
      )}
    </main>
  );
}

/* ---------- kleine presentatie-componenten (module-scope: geen focus-verlies) ---------- */
function Overlay({ children, testid, dim = false }) {
  return (
    <div className={`absolute inset-0 z-20 flex flex-col items-center justify-center gap-2.5 px-4 text-center ${dim ? "bg-black/60" : "bg-[#0A0A0A]/88"}`} data-testid={testid}>
      {children}
    </div>
  );
}
function Big({ children }) {
  return <div className="font-pixel text-[13px] sm:text-[15px] leading-relaxed text-[#F5F3F0] max-w-[420px]">{children}</div>;
}
function Mid({ children, accent = false }) {
  return <div className={`font-pixel text-[9px] sm:text-[10px] leading-relaxed max-w-[400px] ${accent ? "text-[#FFC61A]" : "text-[#DDD6CB]"}`}>{children}</div>;
}
function Tiny({ children }) {
  return <div className="font-pixel text-[7px] text-[#A3A3A3] tracking-wider">{children}</div>;
}
function Logo() {
  return <img src="/johworld/logo.png" alt="JOH World" className="w-44 sm:w-56 my-1" style={{ imageRendering: "pixelated" }} data-testid="jw-logo" />;
}

function PadBtn({ label, testid, onDown, onUp, holdKey }) {
  return (
    <button
      className="w-14 h-14 sm:w-16 sm:h-16 bg-[#2A2A2E] text-[#F5F3F0] font-pixel text-sm rounded-lg border-b-4 border-[#17171B] active:border-b-0 active:translate-y-1 transition-[border,transform] cursor-pointer touch-none"
      onPointerDown={(e) => { e.preventDefault(); onDown(); }}
      onPointerUp={(e) => { e.preventDefault(); onUp(); }}
      onPointerLeave={() => onUp()}
      onPointerCancel={() => onUp()}
      onContextMenu={(e) => e.preventDefault()}
      data-testid={testid}
    >{label}</button>
  );
}

function GearIcon({ label, children }) {
  return (
    <div className="flex flex-col items-center gap-1.5 border border-[#F5F3F0]/15 bg-[#111]/70 px-2 py-2.5">
      {children}
      <span className="font-pixel text-[6px] text-[#A3A3A3]">{label}</span>
    </div>
  );
}

/* Originele pixel-iconen (canvas-vrij, inline SVG-blocks in JOH-stijl) */
function GearGloves() {
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" style={{ imageRendering: "pixelated" }}>
      <rect x="4" y="10" width="10" height="10" fill="#E8362A" /><rect x="16" y="8" width="10" height="10" fill="#E8362A" />
      <rect x="6" y="20" width="6" height="4" fill="#F5F3F0" /><rect x="18" y="18" width="6" height="4" fill="#F5F3F0" />
      <rect x="7" y="12" width="3" height="3" fill="#FF6A5E" /><rect x="19" y="10" width="3" height="3" fill="#FF6A5E" />
    </svg>
  );
}
function GearWraps() {
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" style={{ imageRendering: "pixelated" }}>
      <rect x="6" y="5" width="4" height="20" fill="#E8D9B8" /><rect x="12" y="7" width="4" height="18" fill="#DDD6CB" />
      <rect x="18" y="5" width="4" height="20" fill="#E8D9B8" /><rect x="6" y="12" width="16" height="2" fill="#8C2318" />
    </svg>
  );
}
function GearGuard() {
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" style={{ imageRendering: "pixelated" }}>
      <path d="M6 12 Q15 6 24 12 L22 18 Q15 22 8 18 Z" fill="#E8362A" />
      <rect x="10" y="13" width="10" height="2" fill="#F5F3F0" />
    </svg>
  );
}
function GearWater() {
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" style={{ imageRendering: "pixelated" }}>
      <rect x="11" y="4" width="8" height="4" fill="#4A4A50" /><rect x="10" y="8" width="10" height="16" fill="#5C7A4A" />
      <rect x="10" y="12" width="10" height="8" fill="#7BA05B" /><rect x="12" y="9" width="2" height="14" fill="#F5F3F0" opacity="0.4" />
    </svg>
  );
}

/* ---------- mobiel: detectie + touch-controls (transparant, over het spelbeeld) ---------- */
function useIsMobile() {
  const get = () => window.matchMedia("(pointer: coarse)").matches || window.innerWidth < 768;
  const [m, setM] = useState(get);
  useEffect(() => {
    const mq = window.matchMedia("(pointer: coarse)");
    const on = () => setM(get());
    window.addEventListener("resize", on);
    mq.addEventListener?.("change", on);
    return () => { window.removeEventListener("resize", on); mq.removeEventListener?.("change", on); };
  }, []);
  return m;
}

/* Virtuele joystick linksonder: horizontaal = bewegen, omhoog = springen, omlaag = dukken.
   Doorzichtig zodat het spelbeeld zichtbaar blijft. */
function Joystick({ inputRef }) {
  const baseRef = useRef(null);
  const pidRef = useRef(null);
  const jumpArmed = useRef(true);
  const [knob, setKnob] = useState({ x: 0, y: 0 });
  const R = 34;

  const reset = () => {
    pidRef.current = null;
    setKnob({ x: 0, y: 0 });
    const inp = inputRef.current;
    inp.left = inp.right = inp.down = false;
  };
  const updateFrom = (e) => {
    const rect = baseRef.current.getBoundingClientRect();
    let dx = e.clientX - (rect.left + rect.width / 2);
    let dy = e.clientY - (rect.top + rect.height / 2);
    const len = Math.hypot(dx, dy);
    if (len > R) { dx = (dx / len) * R; dy = (dy / len) * R; }
    setKnob({ x: dx, y: dy });
    const inp = inputRef.current;
    inp.left = dx < -12;
    inp.right = dx > 12;
    inp.down = dy > 16;
    if (dy < -16 && jumpArmed.current) { inp.jumpEdge = true; jumpArmed.current = false; }
    if (dy > -4) jumpArmed.current = true;
  };

  return (
    <div
      ref={baseRef}
      className="absolute bottom-6 left-4 w-28 h-28 rounded-full border-2 border-white/30 bg-white/10 pointer-events-auto touch-none"
      onPointerDown={(e) => { e.preventDefault(); pidRef.current = e.pointerId; e.currentTarget.setPointerCapture?.(e.pointerId); updateFrom(e); }}
      onPointerMove={(e) => { if (pidRef.current === e.pointerId) updateFrom(e); }}
      onPointerUp={reset}
      onPointerCancel={reset}
      onContextMenu={(e) => e.preventDefault()}
      data-testid="jw-joystick"
    >
      <span className="absolute left-1/2 top-1.5 -translate-x-1/2 text-white/40 text-[10px]">▲</span>
      <span className="absolute left-1/2 bottom-1.5 -translate-x-1/2 text-white/40 text-[10px]">▼</span>
      <span className="absolute top-1/2 left-1.5 -translate-y-1/2 text-white/40 text-[10px]">◀</span>
      <span className="absolute top-1/2 right-1.5 -translate-y-1/2 text-white/40 text-[10px]">▶</span>
      <div
        className="absolute left-1/2 top-1/2 w-12 h-12 rounded-full border-2 border-white/45 bg-white/25"
        style={{ transform: `translate(calc(-50% + ${knob.x}px), calc(-50% + ${knob.y}px))` }}
        data-testid="jw-joystick-knob"
      />
    </div>
  );
}

const MBTN = "rounded-full border-2 border-white/35 bg-white/10 text-[#F5F3F0] font-pixel active:bg-white/30 touch-none pointer-events-auto";

/* Actieknoppen rechtsonder: per level wat het spel vereist (L1: spring+jab+cross, L2: PUSH!, L3: 1/2) */
function MobileControls({ state, inputRef, pressNum, pressPush }) {
  if (state === S.L2 || state === S.L2_FAIL) {
    return (
      <div className="absolute inset-x-0 bottom-6 flex justify-center pointer-events-none" data-testid="jw-mobile-controls">
        <button
          className="pointer-events-auto w-32 h-32 rounded-full bg-[#E8362A]/85 border-4 border-[#F5F3F0]/30 font-pixel text-[#F5F3F0] text-base active:scale-95 transition-transform touch-none"
          onPointerDown={(e) => { e.preventDefault(); pressPush(); }}
          data-testid="jw-push-btn"
        >PUSH!</button>
      </div>
    );
  }
  return (
    <div className="absolute inset-0 pointer-events-none" data-testid="jw-mobile-controls">
      <Joystick inputRef={inputRef} />
      <div className="absolute bottom-6 right-4 flex items-end gap-3">
        {state === S.L1 && (
          <>
            <button
              className={`${MBTN} w-14 h-14 text-sm mb-1`}
              onPointerDown={(e) => { e.preventDefault(); inputRef.current.down = true; inputRef.current.duckEdge = true; }}
              onPointerUp={() => { inputRef.current.down = false; }}
              onPointerLeave={() => { inputRef.current.down = false; }}
              onPointerCancel={() => { inputRef.current.down = false; }}
              onContextMenu={(e) => e.preventDefault()}
              data-testid="jw-mbtn-duck"
            >▼</button>
            <button
              className={`${MBTN} w-14 h-14 text-sm mb-1`}
              onPointerDown={(e) => { e.preventDefault(); inputRef.current.jumpEdge = true; }}
              data-testid="jw-mbtn-jump"
            >▲</button>
          </>
        )}
        <button
          className={`${MBTN} w-16 h-16 text-base`}
          onPointerDown={(e) => { e.preventDefault(); pressNum(1); }}
          data-testid="jw-mbtn-1"
        >1</button>
        <button
          className={`${MBTN} w-16 h-16 text-base border-[#E8362A]/60 bg-[#E8362A]/25`}
          onPointerDown={(e) => { e.preventDefault(); pressNum(2); }}
          data-testid="jw-mbtn-2"
        >2</button>
      </div>
    </div>
  );
}
